import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { Me } from '../api';
import { useI18n } from '../i18n';
import { PageHead, PanelShell } from '../layout';
import { IsafDownload } from './reports';

const today = () => new Date().toLocaleDateString('sv-SE');
const monthStart = () => `${today().slice(0, 7)}-01`;
const lastMonth = () => { const d = new Date(); d.setDate(0); return d.toLocaleDateString('sv-SE').slice(0, 7); };

/** Eksportas: the files for the accountant in one place — invoices and lines as CSV, i.SAF, the debt list. */
export function ExportPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [month, setMonth] = useState(lastMonth());
  const period = `from=${from}&to=${to}`;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Eksportas')} text={t('Failai buhalteriui ir apskaitos programoms.')} />}>
      <section className="panel-card export-card">
        <h2>{t('Sąskaitos (CSV)')}</h2>
        <p className="muted">{t('Viena eilutė – viena sąskaita, PVM pagal tarifus stulpeliuose; arba visos sąskaitų eilutės.')}</p>
        <div className="export-period">
          <label><span>{t('Nuo')}</span><input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
          <label><span>{t('Iki')}</span><input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
        </div>
        <div className="export-links">
          <span>{t('Išrašytos')}:</span>
          <a className="button button--ghost button--small" href={`api/v1/export/invoices.csv?direction=outgoing&${period}`} download>{t('sąskaitos')}</a>
          <a className="button button--ghost button--small" href={`api/v1/export/lines.csv?direction=outgoing&${period}`} download>{t('eilutės')}</a>
        </div>
        <div className="export-links">
          <span>{t('Gautos')}:</span>
          <a className="button button--ghost button--small" href={`api/v1/export/invoices.csv?direction=incoming&${period}`} download>{t('sąskaitos')}</a>
          <a className="button button--ghost button--small" href={`api/v1/export/lines.csv?direction=incoming&${period}`} download>{t('eilutės')}</a>
        </div>
      </section>
      <section className="panel-card export-card">
        <h2>i.SAF</h2>
        <p className="muted">{t('Mėnesio PVM sąskaitų registras VMI (PVM mokėtojams).')}</p>
        <div className="export-period">
          <label><span>{t('Mėnuo')}</span><input type="month" value={month} max={today().slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} /></label>
          <IsafDownload month={month} />
        </div>
      </section>
      <section className="panel-card export-card">
        <h2>{t('Skolų žiniaraštis')}</h2>
        <p className="muted">{t('Debitoriai ir kreditoriai šiandien.')}</p>
        <div className="export-links">
          <a className="button button--ghost button--small" href={`api/v1/reconciliation/ledger.pdf?date=${today()}`}>PDF</a>
          <a className="button button--ghost button--small" href={`api/v1/reconciliation/ledger.csv?date=${today()}`}>CSV</a>
        </div>
      </section>
      <RivileExport />
      <RivileErpExport />
      <section className="panel-card export-card is-soon">
        <h2>Site.pro (B1) <span className="shop-tag">{t('Netrukus')}</span></h2>
        <p className="muted">{t('Site.pro importuoja sąskaitas iš savo Excel šablono, kurį galima atsisiųsti tik prisijungus prie Site.pro. Jei dirbate su Site.pro, atsiųskite mums tuščią pardavimų ir pirkimų šabloną — paruošime eksportą.')}</p>
        <Link to="/support" className="button button--ghost button--small">{t('Parašyti mums')}</Link>
      </section>
    </PanelShell>
  );
}

/** The accountant's Rivilė codes, kept in this browser (they are the same every month). */
const RIVILE_KEY = 'fsp-rivile-export';
const loadRivile = () => {
  try { return { itemCode: 'PASLAUGA', department: '01', journal: 'DK', encoding: 'utf8', ...JSON.parse(localStorage.getItem(RIVILE_KEY) ?? '{}') } as { itemCode: string; department: string; journal: string; encoding: 'utf8' | 'cp1257' }; } catch { return { itemCode: 'PASLAUGA', department: '01', journal: 'DK', encoding: 'utf8' as const }; }
};

/**
 * Rivilė GAMA: the period's partners, sales and purchases as .eip files for "Išorinis importas" — the partners
 * first (a document's client must exist), then the documents. The service and department codes are the
 * accountant's own in Rivilė.
 */
function RivileExport() {
  const { t } = useI18n();
  const [from, setFrom] = useState(() => `${lastMonth()}-01`);
  const [to, setTo] = useState(() => { const [y, m] = lastMonth().split('-').map(Number) as [number, number]; return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); });
  const [opts, setOpts] = useState(loadRivile);
  useEffect(() => { try { localStorage.setItem(RIVILE_KEY, JSON.stringify(opts)); } catch { /* private mode */ } }, [opts]);
  const query = (what: string) => `api/v1/export/rivile.eip?${new URLSearchParams({ what, from, to, itemCode: opts.itemCode || 'PASLAUGA', department: opts.department || '01', journal: opts.journal || 'DK', encoding: opts.encoding })}`;
  return (
    <section className="panel-card export-card">
      <h2>Rivilė GAMA</h2>
      <p className="muted">{t('Failai Rivilės „Išoriniam importui“ (.eip): pirmiausia partneriai, tada pardavimai ir pirkimai.')}</p>
      <div className="export-period">
        <label><span>{t('Nuo')}</span><input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
        <label><span>{t('Iki')}</span><input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
      </div>
      <div className="export-period">
        <label><span>{t('Paslaugos kodas Rivilėje')}</span><input value={opts.itemCode} maxLength={12} onChange={(e) => setOpts({ ...opts, itemCode: e.target.value })} /></label>
        <label><span>{t('Padalinys')}</span><input value={opts.department} maxLength={12} onChange={(e) => setOpts({ ...opts, department: e.target.value })} /></label>
        <label><span>{t('DK žurnalo kodas')}</span><input value={opts.journal} maxLength={12} onChange={(e) => setOpts({ ...opts, journal: e.target.value })} /></label>
        <label><span>{t('Koduotė')}</span><select value={opts.encoding} onChange={(e) => setOpts({ ...opts, encoding: e.target.value as 'utf8' | 'cp1257' })}><option value="utf8">UTF-8</option><option value="cp1257">Windows-1257</option></select></label>
      </div>
      <div className="export-links">
        <a className="button button--ghost button--small" href={query('partners')} download>1. {t('Partneriai')}</a>
        <a className="button button--ghost button--small" href={query('sales')} download>2. {t('Pardavimai')}</a>
        <a className="button button--ghost button--small" href={query('purchases')} download>3. {t('Pirkimai')}</a>
      </div>
      <div className="export-links">
        <span>{t('Arba')}</span>
        <a className="button button--ghost button--small" href={query('journal')} download>{t('Didžiosios knygos operacijos (DK)')}</a>
      </div>
      <p className="muted account-note">{t('Dokumentai Rivilėje patys sukuria DK įrašus, todėl importuokite arba dokumentus (1–3), arba mūsų žurnalo operacijas (DK) — ne abu. DK operacijoms Rivilėje turi būti tos pačios sąskaitos kaip mūsų sąskaitų plane.')}</p>
      <p className="muted account-note">{t('Paslaugos kodas ir padalinys turi būti sukurti Rivilėje. Jei lietuviškos raidės importuojamos neteisingai, pasirinkite Windows-1257.')}</p>
    </section>
  );
}

/**
 * Rivilė ERP (cloud): its Excel templates — the clients and the journal as ledger documents. ERP takes no EIP
 * and no whole invoices by Excel, so the documents go in as the general ledger entries they make.
 */
function RivileErpExport() {
  const { t } = useI18n();
  const [from, setFrom] = useState(() => `${lastMonth()}-01`);
  const [to, setTo] = useState(() => { const [y, m] = lastMonth().split('-').map(Number) as [number, number]; return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); });
  const [journal, setJournal] = useState('0001');
  const [pad, setPad] = useState(false);
  const url = (what: string) => `api/v1/export/rivile-erp.xlsx?${new URLSearchParams({ what, from, to, journal: journal || '0001', pad: pad ? '1' : '0' })}`;
  return (
    <section className="panel-card export-card">
      <h2>Rivilė ERP <span className="shop-tag">{t('debesyje')}</span></h2>
      <p className="muted">{t('Rivilė ERP importuoja Excel šablonus: klientus ir didžiosios knygos (DK) žurnalinius įrašus. Visų sąskaitų su PVM per Excel ERP neimportuoja, todėl mūsų žurnalas perkeliamas kaip DK įrašai.')}</p>
      <div className="export-period">
        <label><span>{t('Nuo')}</span><input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
        <label><span>{t('Iki')}</span><input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
        <label><span>{t('DK žurnalo kodas')}</span><input value={journal} maxLength={20} onChange={(e) => setJournal(e.target.value)} /></label>
      </div>
      <label className="recurring-check"><input type="checkbox" checked={pad} onChange={(e) => setPad(e.target.checked)} />{t('Sąskaitų kodus papildyti nuliais iki 5 skaitmenų (pvz., 2410 → 24100)')}</label>
      <div className="export-links">
        <a className="button button--ghost button--small" href={url('clients')} download>1. {t('Klientai (Excel)')}</a>
        <a className="button button--ghost button--small" href={url('journal')} download>2. {t('DK žurnaliniai įrašai (Excel)')}</a>
      </div>
      <p className="muted account-note">{t('ERP: Nustatymai → Pardavimai ir pirkimai → Klientai → Importuoti; Apskaita → Didžioji knyga → DK žurnaliniai įrašai → Importuoti. Sąskaitų planas ERP turi atitikti mūsų sąskaitų kodus.')}</p>
    </section>
  );
}
